// Term Loan Agency verification (legacy /AccountsApproval/VerifyTermLoanAgency).
// Pending agencies are verified/approved via the shared status list; agencies returned for update
// (Status '0') get legacy's inline resubmit form (Agency Name + Address editable).
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Handshake, Clock, RotateCcw, Send } from 'lucide-react';

import InboxHeader from '../../components/Inbox/InboxHeader';
import ActionButtons from '../../components/Inbox/ActionButtons';
import RemarksHistory from '../../components/Inbox/RemarksHistory';
import InboxSplitLayout from '../../components/Inbox/InboxSplitLayout';
import { SimpleCommentInput } from '../../components/Inbox/VerificationInput';

import {
    fetchVerifyTLAgencyQueue,
    fetchVerifyTLAgencyDetail,
    submitApproveTLAgency,
    submitUpdateTLAgency,
    clearVerifyTLAgencyDetail,
    resetVerifyTLAgency,
    selectVTAQueue, selectVTAQueueLoading, selectVTAQueueError,
    selectVTADetail, selectVTADetailLoading, selectVTADetailError,
    selectVTAVerifyLoading, selectVTAUpdateLoading,
} from '../../slices/accountsSlice/verifyTLAgencySlice';

import {
    fetchStatusList,
    selectEnabledActions,
    selectHasActions,
    selectStatusListLoading,
    selectStatusListError,
    resetApprovalData,
    setShowReturnButton,
} from '../../slices/CommonSlice/getStatusSlice';

import {
    fetchRemarks,
    selectRemarks,
    selectRemarksLoading,
    setSelectedMOID,
} from '../../slices/supplierPOSlice/purcahseHelperSlice';

const ACTION_VERB = {
    Verify: 'Verified Successfully',
    Approve: 'Approved Successfully',
    Return: 'Returned for Update',
    Reject: 'Rejected Successfully',
};

const Field = ({ label, value, className = '' }) => (
    <div className={className}>
        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-0.5">{label}</p>
        <p className="text-sm font-semibold text-gray-800 dark:text-gray-100 break-words">{value || value === 0 ? value : '—'}</p>
    </div>
);

const StatusBadge = ({ status }) => (String(status) === '0' ? (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300 whitespace-nowrap">Return</span>
) : (
    <span className="px-2 py-0.5 text-xs rounded-full border bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 whitespace-nowrap">Verification Pending</span>
));

const inputCls = 'w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500';
const labelCls = 'block text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1';

const VerifyTLAgency = ({ notificationData, onNavigate }) => {
    const dispatch = useDispatch();
    const { userData, userDetails } = useSelector((s) => s.auth);
    const roleId = userData?.roleId || userData?.RID;

    const queue = useSelector(selectVTAQueue);
    const queueLoading = useSelector(selectVTAQueueLoading);
    const queueError = useSelector(selectVTAQueueError);
    const detail = useSelector(selectVTADetail);
    const detailLoading = useSelector(selectVTADetailLoading);
    const detailError = useSelector(selectVTADetailError);
    const verifyLoading = useSelector(selectVTAVerifyLoading);
    const updateLoading = useSelector(selectVTAUpdateLoading);

    const statusLoading = useSelector(selectStatusListLoading);
    const statusError = useSelector(selectStatusListError);
    const enabledActions = useSelector(selectEnabledActions);
    const hasActions = useSelector(selectHasActions);

    const remarks = useSelector(selectRemarks);
    const remarksLoading = useSelector(selectRemarksLoading);

    const { InboxTitle, ModuleDisplayName } = notificationData || {};

    const [selectedItem, setSelectedItem] = useState(null);
    const [comment, setComment] = useState('');
    const [showRemarksHistory, setShowRemarksHistory] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
    const [isLeftPanelHovered, setIsLeftPanelHovered] = useState(false);
    const [updName, setUpdName] = useState('');
    const [updAddress, setUpdAddress] = useState('');

    const getCurrentUser = () => userData?.userName || userDetails?.userName || 'system';

    const isReturned = String(selectedItem?.Status) === '0';
    // Only trust the detail that belongs to the row currently selected.
    const agency = selectedItem && detail?.AgencyId === selectedItem.AgencyId ? detail : null;

    useEffect(() => {
        if (roleId) dispatch(fetchVerifyTLAgencyQueue(roleId));
    }, [roleId, dispatch]);

    useEffect(() => {
        // Legacy keeps "Return" (dropped only, with Reject, for agencies being closed — see ActionButtons).
        dispatch(setShowReturnButton('Yes'));
        return () => {
            dispatch(resetVerifyTLAgency());
            dispatch(resetApprovalData());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!selectedItem) return;
        dispatch(clearVerifyTLAgencyDetail());
        dispatch(resetApprovalData());
        dispatch(setShowReturnButton('Yes'));
        dispatch(fetchVerifyTLAgencyDetail(selectedItem.AgencyId));
        setComment('');
        setShowRemarksHistory(false);
        setIsLeftPanelCollapsed(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedItem]);

    // The queue row carries no MOID; the detail does. Remarks are keyed on the agency code.
    useEffect(() => {
        if (!agency?.MOID || !roleId) return;
        if (!isReturned) dispatch(fetchStatusList({ MOID: agency.MOID, ROID: roleId, ChkAmt: 0 }));
        dispatch(setSelectedMOID(agency.MOID));
        dispatch(fetchRemarks({ trno: agency.AgencyId, moid: agency.MOID }));
        setUpdName(agency.AgencyName || '');
        setUpdAddress(agency.AgencyAddress || '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agency?.MOID, agency?.AgencyId]);

    const handleBackToInbox = () => {
        if (onNavigate) onNavigate('dashboard', { name: 'Dashboard', type: 'dashboard' });
    };

    const handleRefresh = () => {
        if (roleId) dispatch(fetchVerifyTLAgencyQueue(roleId));
    };

    const finishAndRefresh = () => {
        setTimeout(() => {
            dispatch(fetchVerifyTLAgencyQueue(roleId));
            setSelectedItem(null);
            setComment('');
            setShowRemarksHistory(false);
            setIsLeftPanelCollapsed(false);
            dispatch(clearVerifyTLAgencyDetail());
            dispatch(resetApprovalData());
        }, 800);
    };

    const handleActionClick = async (action) => {
        if (!agency) { toast.error('No agency selected'); return; }
        if (!comment.trim()) { toast.error('Enter Note'); return; }

        const actionValue = action.type;
        const payload = {
            RoleId: roleId,
            Createdby: getCurrentUser(),
            AgencyId: agency.AgencyId,
            Action: actionValue,
            RemarksNote: comment.trim(),
            AgencyStatus: agency.AgencyStatus,
        };

        try {
            const result = await dispatch(submitApproveTLAgency(payload)).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            if (status === 'Submitted' || status === 'Submited') {
                toast.success(`Term Loan Agency ${ACTION_VERB[actionValue] || `${actionValue}d`}`);
                finishAndRefresh();
                return;
            }
            toast.error(status || `${actionValue} was not applied — the server returned no confirmation.`);
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Failed to submit agency verification');
        }
    };

    const handleResubmit = async () => {
        if (!agency) return;
        const errors = [];
        if (!updName.trim()) errors.push('Enter Agency Name');
        if (!updAddress.trim()) errors.push('Enter Agency Address');
        if (errors.length) { errors.forEach((m) => toast.error(m)); return; }

        const payload = {
            AgencyId: agency.AgencyId,
            RoleId: roleId,
            Createdby: getCurrentUser(),
            Action: 'Update',
            AgencyName: updName.trim(),
            AgencyAddress: updAddress.trim(),
            CheckUpdationType: 'ReturnUpdate',
        };

        try {
            const result = await dispatch(submitUpdateTLAgency(payload)).unwrap();
            const status = typeof result === 'string' ? result : result?.Data;
            if (status === 'Submitted' || status === 'Submited') {
                toast.success('Term Loan Agency Updated Successfully');
                finishAndRefresh();
                return;
            }
            toast.error(status || 'Update was not applied — the server returned no confirmation.');
        } catch (error) {
            toast.error(typeof error === 'string' ? error : 'Failed to update agency');
        }
    };

    const filteredQueue = queue.filter((item) => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        return item.AgencyId?.toLowerCase().includes(q)
            || item.AgencyName?.toLowerCase().includes(q)
            || item.NatureGroupName?.toLowerCase().includes(q);
    });

    const renderItemCard = (item) => (
        <div className="p-4">
            <div className="flex items-center space-x-3 mb-2">
                <div className="w-12 h-12 rounded-full border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
                    <Handshake className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm truncate">{item.AgencyName}</h3>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.AgencyId}</p>
                </div>
            </div>
            <div className="flex items-center justify-between gap-2 text-xs">
                <span className="px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-700 text-[10px] text-gray-600 dark:text-gray-300 truncate">{item.NatureGroupName}</span>
                <StatusBadge status={item.Status} />
            </div>
        </div>
    );

    const renderListItem = (item) => (
        <div className="flex items-center gap-x-6 gap-y-1 flex-wrap text-sm">
            <span className="font-semibold text-gray-900 dark:text-white min-w-[200px] truncate">{item.AgencyName}</span>
            <span className="font-mono bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded text-xs">{item.AgencyId}</span>
            <span className="text-gray-500 dark:text-gray-400 truncate max-w-[240px]">{item.NatureGroupName}</span>
            <span className="ml-auto"><StatusBadge status={item.Status} /></span>
        </div>
    );

    const renderCollapsedItem = () => (
        <div className="w-full h-full rounded-lg border-2 border-indigo-200 dark:border-indigo-600 bg-gradient-to-br from-indigo-100 to-indigo-100 dark:from-indigo-800/50 dark:to-indigo-800/50 flex items-center justify-center">
            <Handshake className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
        </div>
    );

    const renderDetails = () => (
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-5 border-2 border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <Field label="Agency Code" value={agency.AgencyId} />
                {!isReturned && <Field label="Agency Name" value={agency.AgencyName} />}
                {!isReturned && <Field label="Agency Address" value={agency.AgencyAddress} className="sm:col-span-2" />}
                <Field label="Nature of Group" value={agency.NatureGroupName} />
                <Field label="Group Name" value={agency.GroupName} />
                {agency.SubGroupId && agency.SubGroupId !== '0' && <Field label="Sub Group Name" value={agency.SubGroupName} />}
                {!isReturned && <Field label="Agency Status" value={agency.AgencyStatus} />}
            </div>
        </div>
    );

    const renderResubmitForm = () => (
        <div className="bg-amber-50 dark:bg-amber-900/20 rounded-2xl p-5 border-2 border-amber-200 dark:border-amber-700 space-y-4">
            <div className="flex items-center gap-2">
                <RotateCcw className="w-4 h-4 text-amber-700 dark:text-amber-400" />
                <h3 className="text-sm font-bold text-amber-800 dark:text-amber-300">Returned for Update — correct and resubmit</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                    <label className={labelCls}>Agency Name <span className="text-red-500">*</span></label>
                    <input type="text" className={inputCls} value={updName} onChange={(e) => setUpdName(e.target.value)} />
                </div>
                <div>
                    <label className={labelCls}>Agency Address <span className="text-red-500">*</span></label>
                    <input type="text" className={inputCls} value={updAddress} onChange={(e) => setUpdAddress(e.target.value)} />
                </div>
            </div>
            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={handleResubmit}
                    disabled={updateLoading}
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                    {updateLoading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> : <Send className="w-4 h-4" />}
                    Resubmit
                </button>
            </div>
        </div>
    );

    const renderActions = () => {
        if (statusLoading) {
            return (
                <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-center space-x-3">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                        <span className="text-gray-600 dark:text-gray-400">Loading actions…</span>
                    </div>
                </div>
            );
        }
        if (statusError) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ Error loading actions: {statusError}</p>
                </div>
            );
        }
        if (!hasActions || !enabledActions || enabledActions.length === 0) {
            return (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-6 border border-yellow-200 dark:border-yellow-700">
                    <p className="text-yellow-700 dark:text-yellow-400 text-center">ℹ️ No actions available for this agency</p>
                </div>
            );
        }
        return (
            <ActionButtons
                actions={enabledActions}
                onActionClick={handleActionClick}
                loading={verifyLoading}
                isVerified={true}
                comment={comment}
                showValidation={true}
                // Legacy drops Return and Reject from the dropdown when the agency is being closed.
                excludeActions={agency.AgencyStatus === 'Closed' ? ['return', 'reject'] : []}
            />
        );
    };

    const renderDetailContent = () => {
        if (!selectedItem) return null;

        if (detailLoading || (!agency && !detailError)) {
            return (
                <div className="flex items-center justify-center gap-3 py-12">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-600"></div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Loading agency details…</span>
                </div>
            );
        }
        if (detailError || !agency) {
            return (
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-6 border border-red-200 dark:border-red-700">
                    <p className="text-red-600 dark:text-red-400 text-center">⚠️ {detailError || 'Agency details could not be loaded — it may no longer be pending at your level.'}</p>
                </div>
            );
        }

        return (
            <div className="space-y-6">
                <div className="bg-gradient-to-r from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20 rounded-2xl p-6 border-2 border-indigo-200 dark:border-indigo-700">
                    <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-1">{agency.AgencyName}</h2>
                    <p className="text-indigo-600 dark:text-indigo-400 font-semibold mb-3">{agency.AgencyId}</p>
                    <div className="flex flex-wrap gap-2">
                        {agency.AgencyStatus && (
                            <span className="px-3 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-full text-xs font-medium">Agency Status: {agency.AgencyStatus}</span>
                        )}
                        <StatusBadge status={selectedItem.Status} />
                    </div>
                </div>

                {renderDetails()}

                <RemarksHistory
                    isOpen={showRemarksHistory}
                    onToggle={() => setShowRemarksHistory(!showRemarksHistory)}
                    remarks={remarks}
                    loading={remarksLoading}
                    title="Approval History"
                />

                {isReturned ? renderResubmitForm() : (
                    <>
                        <SimpleCommentInput
                            comment={comment}
                            onCommentChange={(e) => setComment(e.target.value)}
                            label="Notes"
                            placeholder="Enter verification notes…"
                            required
                            rows={3}
                            showCharCount
                        />
                        {renderActions()}
                    </>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-6">
            <InboxHeader
                title={`${InboxTitle || 'Term Loan Agency Verification'} (${queue.length})`}
                subtitle={ModuleDisplayName}
                itemCount={queue.length}
                onBackClick={handleBackToInbox}
                HeaderIcon={Handshake}
                badgeText="TL Agency Verification"
                badgeCount={queue.length}
                searchConfig={{
                    enabled: true,
                    placeholder: 'Search by agency code, name, nature of group…',
                    value: searchQuery,
                    onChange: (e) => setSearchQuery(e.target.value),
                }}
                enableViewToggle
            />

            <InboxSplitLayout
                isLeftPanelCollapsed={isLeftPanelCollapsed}
                onLeftPanelCollapseToggle={setIsLeftPanelCollapsed}
                isLeftPanelHovered={isLeftPanelHovered}
                onLeftPanelHoverChange={setIsLeftPanelHovered}
                left={{
                    items: filteredQueue,
                    selectedItem,
                    onItemSelect: setSelectedItem,
                    renderItem: renderItemCard,
                    renderListItem,
                    renderCollapsedItem,
                    loading: queueLoading,
                    error: queueError,
                    onRefresh: handleRefresh,
                    config: {
                        title: 'Pending',
                        icon: Clock,
                        emptyMessage: 'No term loan agencies pending verification!',
                        itemKey: 'AgencyId',
                        enableCollapse: true,
                        enableRefresh: true,
                        enableHover: true,
                        maxHeight: '100%',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                    },
                    renderPopupContent: () => renderDetailContent(),
                    popupConfig: {
                        title: 'Term Loan Agency Verification',
                        icon: Handshake,
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxWidth: 'max-w-[80vw]',
                    },
                }}
                right={{
                    selectedItem,
                    loading: false,
                    renderContent: renderDetailContent,
                    config: {
                        title: 'Agency Details',
                        icon: Handshake,
                        selectedTitle: 'Term Loan Agency Verification',
                        emptyTitle: 'No Agency Selected',
                        emptyMessage: 'Select an agency from the list to view details and take action.',
                        headerGradient: 'from-indigo-50 to-blue-50 dark:from-indigo-900/20 dark:to-blue-900/20',
                        maxHeight: 'calc(100vh-200px)',
                        sticky: true,
                        stickyTop: '1.5rem',
                    },
                }}
            />
        </div>
    );
};

export default VerifyTLAgency;
